import PropTypes from 'prop-types';
import { forwardRef, RefObject, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { ListItemButton, ListItemIcon, ListItemText, Typography, alpha } from '@mui/material';
import { MenuSlice } from 'store/menu.ts';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from 'store/store.ts';
import { SingleMenuItem } from 'types.ts';

type Props = {
  item: SingleMenuItem;
  level: number;
};

const selector = (state: MenuSlice) => ({
  drawerOpen: state.drawerOpen,
  openItem: state.openItem,
  setActiveItem: state.setActiveItem,
});
const NavItem = ({ item, level }: Props) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { drawerOpen, openItem, setActiveItem } = useStore(useShallow(selector));

  const listItemProps = {
    // eslint-disable-next-line react/display-name
    component: forwardRef((props, ref) => <Link ref={ref as RefObject<HTMLAnchorElement>} {...props} to={item.url} />),
  };

  const itemHandler = (id: string) => {
    setActiveItem([id]);
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} /> : false;

  const isSelected = openItem.findIndex((id) => id === item.id) > -1;
  // active menu item on page load
  useEffect(() => {
    if (pathname.includes(item.url)) {
      setActiveItem([item.id]);
    }
    // eslint-disable-next-line
  }, [pathname]);

  const inactiveColor = 'text.secondary';
  const activeColor = 'text.primary';

  const hoverBg = alpha(theme.palette.primary.main, 0.08);
  const selectedBg = alpha(theme.palette.primary.main, 0.16);
  const selectedHoverBg = alpha(theme.palette.primary.main, 0.22);

  // TODO Move to styled component
  return (
    <ListItemButton
      {...listItemProps}
      onClick={() => itemHandler(item.id)}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        pl: drawerOpen ? `${level * 28}px` : 1.5,
        py: !drawerOpen && level === 1 ? 1.25 : 1,
        my: 0.5,
        color: inactiveColor,
        '&.Mui-selected::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '20%',
          bottom: '20%',
          width: 3,
          borderTopRightRadius: 3,
          borderBottomRightRadius: 3,
          backgroundColor: theme.palette.accent.main,
          boxShadow: `0 0 12px ${alpha(theme.palette.accent.main, 0.6)}`,
        },
        ...(drawerOpen && {
          mx: 1,
          borderRadius: '10px',
          '&:hover': {
            bgcolor: hoverBg,
          },
          '&.Mui-selected': {
            bgcolor: selectedBg,
            color: activeColor,
            '&:hover': {
              color: activeColor,
              bgcolor: selectedHoverBg,
            },
          },
        }),
        ...(!drawerOpen && {
          '&:hover': {
            bgcolor: 'transparent',
          },
          '&.Mui-selected': {
            '&:hover': {
              bgcolor: 'transparent',
            },
            bgcolor: 'transparent',
          },
        }),
      }}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            minWidth: 28,
            color: isSelected ? activeColor : inactiveColor,
            ...(!drawerOpen && {
              borderRadius: 1.5,
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: hoverBg,
              },
            }),
            ...(!drawerOpen &&
              isSelected && {
                bgcolor: selectedBg,
                '&:hover': {
                  bgcolor: selectedBg,
                },
              }),
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}
      {drawerOpen && <ListItemText primary={<Typography variant="body1">{item.title}</Typography>} />}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number,
};

export default NavItem;
